import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Download, Loader2, Eye, FileText, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { buildInvoicePDF, invoicePdfBlobUrl } from '@/lib/invoicePdf';
import api from '@/lib/api';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  sent: 'bg-blue-100 text-blue-700 border-blue-200',
  paid: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  overdue: 'bg-red-100 text-red-700 border-red-200',
  cancelled: 'bg-slate-100 text-slate-500 border-slate-200',
};

/**
 * ViewInvoiceDialog — WYSIWYG preview of the invoice PDF.
 *
 * The dialog embeds the SAME jsPDF blob that gets emailed / downloaded,
 * so managers see byte-identical output before sending. Rendering the
 * blob in an <iframe> uses the browser's built-in PDF viewer, which
 * gives page navigation, zoom and print for free.
 */
export const ViewInvoiceDialog = ({ open, onOpenChange, invoice, onDownloadPDF, onInvoiceSent }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [buildError, setBuildError] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendForm, setSendForm] = useState({ to: '', subject: '', message: '' });

  useEffect(() => {
    if (!open || !invoice) {
      setPdfUrl(null);
      setBuildError(null);
      setSendOpen(false);
      return undefined;
    }
    let url;
    try {
      url = invoicePdfBlobUrl(invoice);
      setPdfUrl(url);
      setBuildError(null);
    } catch (err) {
      console.error('Invoice PDF build failed:', err);
      setBuildError(err?.message || 'Failed to build invoice preview');
    }
    // Prefill send form
    setSendForm({
      to: invoice.client_email || '',
      subject: `Invoice ${invoice.invoice_number || ''} — ${invoice.property_name || ''}`.trim(),
      message: invoice.message_to_client || '',
    });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, invoice]);

  const openSendPanel = () => setSendOpen(true);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    if (!invoice) return;
    if (!sendForm.to.trim()) {
      toast.error('Enter a recipient email');
      return;
    }
    setSending(true);
    try {
      // Build the SAME PDF the manager just previewed, then base64 it.
      const { doc } = buildInvoicePDF(invoice);
      const dataUri = doc.output('datauristring'); // "data:application/pdf;base64,..."
      const base64 = dataUri.split(',', 2)[1] || '';
      const res = await api.post(`/invoices/${invoice.id}/send`, {
        pdf_base64: base64,
        to: sendForm.to.trim(),
        subject: sendForm.subject.trim() || undefined,
        message: sendForm.message.trim() || undefined,
      });
      toast.success(res.data?.message || 'Invoice emailed');
      setSendOpen(false);
      onInvoiceSent?.(invoice.id);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to send invoice';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-[95vw] sm:max-w-[880px] h-[92dvh] sm:h-[88dvh] max-h-[92dvh] p-0 gap-0 flex flex-col overflow-hidden print:max-w-full print:max-h-none"
        data-testid="view-invoice-dialog"
      >
        <DialogHeader className="px-4 sm:px-6 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-5 w-5 text-slate-500 flex-shrink-0" />
              <div className="min-w-0">
                <DialogTitle className="font-manrope text-lg sm:text-xl truncate">
                  Invoice {invoice?.invoice_number || ''}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-slate-500 truncate">
                    {invoice?.property_name}
                  </span>
                  {invoice?.status && (
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-medium border ${STATUS_COLORS[invoice.status] || STATUS_COLORS.draft}`}
                    >
                      {invoice.status}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500">
                <Eye className="h-3.5 w-3.5" />
                Preview matches emailed PDF exactly
              </span>
              {invoice && (
                <Button
                  size="sm"
                  variant="default"
                  onClick={openSendPanel}
                  data-testid="preview-send-invoice-btn"
                >
                  <Send className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Send to client</span>
                  <span className="sm:hidden">Send</span>
                </Button>
              )}
              {onDownloadPDF && invoice && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onDownloadPDF(invoice)}
                  data-testid="preview-download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Download</span>
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-slate-100 relative" data-testid="invoice-pdf-preview-body">
          {sendOpen && (
            <div className="absolute inset-0 z-10 bg-white/95 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto" data-testid="send-invoice-panel">
              <div className="max-w-md mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                    <Send className="h-4 w-4" /> Send invoice to client
                  </h3>
                  <button
                    onClick={() => setSendOpen(false)}
                    className="p-1 rounded hover:bg-slate-100"
                    aria-label="Close"
                    data-testid="close-send-panel-btn"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <form className="space-y-3" onSubmit={handleSend}>
                  <div className="space-y-1">
                    <Label htmlFor="send-to">Recipient email</Label>
                    <Input
                      id="send-to"
                      type="email"
                      required
                      value={sendForm.to}
                      onChange={(e) => setSendForm((p) => ({ ...p, to: e.target.value }))}
                      placeholder="client@example.com"
                      data-testid="send-invoice-to"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="send-subject">Subject</Label>
                    <Input
                      id="send-subject"
                      value={sendForm.subject}
                      onChange={(e) => setSendForm((p) => ({ ...p, subject: e.target.value }))}
                      data-testid="send-invoice-subject"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="send-message">Message (optional)</Label>
                    <Textarea
                      id="send-message"
                      rows={4}
                      value={sendForm.message}
                      onChange={(e) => setSendForm((p) => ({ ...p, message: e.target.value }))}
                      placeholder="Thanks for the work this month..."
                      data-testid="send-invoice-message"
                    />
                  </div>
                  <p className="text-xs text-slate-500">
                    The PDF you&apos;re previewing is attached. Delivery is logged in
                    Email Health for audit.
                  </p>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setSendOpen(false)}
                      disabled={sending}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={sending}
                      data-testid="send-invoice-submit"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-1" />
                          Send now
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {buildError ? (
            <div className="h-full flex items-center justify-center p-6 text-sm text-red-600">
              {buildError}
            </div>
          ) : !pdfUrl ? (
            <div className="h-full flex items-center justify-center text-slate-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <iframe
                key={pdfUrl}
                src={pdfUrl}
                title={`Invoice ${invoice?.invoice_number} preview`}
                className="w-full h-full border-0 block"
                data-testid="invoice-pdf-iframe"
              />
              {/* Fallback affordance for viewers that can't display PDFs
                  inline (mobile Safari, some in-app browsers). The iframe
                  covers this on modern desktop browsers so it just sits
                  underneath doing nothing. */}
              <div className="absolute inset-x-0 bottom-0 pointer-events-none flex justify-center pb-3">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pointer-events-auto inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/95 border border-slate-200 shadow-sm text-slate-700 hover:bg-white"
                  data-testid="open-pdf-new-tab"
                >
                  Open PDF in a new tab
                </a>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewInvoiceDialog;
